<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useTemplateStore } from "@/stores/template";
import { menuForRole } from '@/data/menus.js';

// Main store and Route
const store = useTemplateStore();
const route = useRoute();

// Component properties
const props = defineProps({
  nodes: {
    type: Array,
    description: "The nodes of the navigation",
  },
  subMenu: {
    type: Boolean,
    default: false,
    description: "If true, a submenu will be rendered",
  },
  dark: {
    type: Boolean,
    default: false,
    description: "Dark mode for menu",
  },
  horizontal: {
    type: Boolean,
    default: false,
    description: "Horizontal menu in large screen width",
  },
  horizontalHover: {
    type: Boolean,
    default: false,
    description: "Hover mode for horizontal menu",
  },
  horizontalCenter: {
    type: Boolean,
    default: false,
    description: "Center mode for horizontal menu",
  },
  horizontalJustify: {
    type: Boolean,
    default: false,
    description: "Justify mode for horizontal menu",
  },
  disableClick: {
    type: Boolean,
    default: false,
    description:
      "Disables submenu click on 2+ level when we are in horizontal and hover mode",
  },
});

// Set CSS classes accordingly
const classContainer = computed(() => {
  return {
    "nav-main": !props.subMenu,
    "nav-main-submenu": props.subMenu,
    "nav-main-dark": props.dark,
    "nav-main-horizontal": props.horizontal,
    "nav-main-hover": props.horizontalHover,
    "nav-main-horizontal-center": props.horizontalCenter,
    "nav-main-horizontal-justify": props.horizontalJustify,
  };
});

// Checks if a submenu path is part of the URL path
function subIsActive(paths) {
  const activePaths = Array.isArray(paths) ? paths : [paths];

  return activePaths.some((path) => {
    return route.path.indexOf(path) === 0; // current path starts with this path string
  });
}

// Main menu toggling and mobile functionality
function linkClicked(e, submenu) {
  if (submenu) {
    // Get closest li element
    let el = e.target.closest("li");

    // Check if we are in a large screen, have horizontal navigation and hover is enabled
    if (
      !(
        window.innerWidth > 991 &&
        ((props.horizontal && props.horizontalHover) || props.disableClick)
      )
    ) {
      if (el.classList.contains("open")) {
        // If submenu is open, close it..
        el.classList.remove("open");
      } else {
        // .. else if submenu is closed, close all other (same level) submenus first before open it
        Array.from(el.closest("ul").children).forEach((element) => {
          element.classList.remove("open");
        });

        el.classList.add("open");
      }
    }
  } else {
    // If we are in mobile, close the sidebar
    if (window.innerWidth < 992) {
      store.sidebar({ mode: "close" });
    }
  }
}

// Ссылки для навигации на основе API маршрутов
// Меню зависит от роли вошедшего пользователя
const routerLinks = menuForRole()

// Текущие открытые подменю
const openSubmenus = ref([]);

// Переключение подменю
function toggleSubmenu(id) {
  if (openSubmenus.value.includes(id)) {
    openSubmenus.value = openSubmenus.value.filter(item => item !== id);
  } else {
    openSubmenus.value.push(id);
  }
}

// Проверка активен ли пункт меню
function isActive(link) {
  return route.path === link || route.path.startsWith(link + '/');
}

// Проверка активен ли родительский пункт меню с подменю
function isParentActive(subLinks) {
  return subLinks.some(item => isActive(item.link));
}
</script>

<template>
  <ul :class="classContainer" class="nav-main">
    <li v-for="item in routerLinks" :key="item.id" :class="{
      'nav-main-item': true,
      'open': item.sub && openSubmenus.includes(item.id),
      'active': item.sub ? isParentActive(item.sub) : isActive(item.link)
    }">
      <!-- Пункт меню без подменю -->
      <template v-if="!item.sub">
        <RouterLink
          class="nav-main-link"
          :to="item.link"
          :class="{ 'active': isActive(item.link) }"
          @click="linkClicked($event, false)"
        >
          <i v-if="item.icon" :class="`nav-main-link-icon ${item.icon}`"></i>
          <span class="nav-main-link-name">{{ item.name }}</span>
        </RouterLink>
      </template>

      <!-- Пункт меню с подменю -->
      <template v-else>
        <a
          href="#"
          class="nav-main-link nav-main-link-submenu"
          @click.prevent="toggleSubmenu(item.id)"
        >
          <i v-if="item.icon" :class="`nav-main-link-icon ${item.icon}`"></i>
          <span class="nav-main-link-name">{{ item.name }}</span>
          <i class="nav-main-link-icon fa fa-chevron-down"></i>
        </a>
        
        <!-- Подменю -->
        <ul v-if="openSubmenus.includes(item.id)" class="nav-main-submenu">
          <li v-for="subItem in item.sub" :key="subItem.id" class="nav-main-item">
            <RouterLink
              class="nav-main-link"
              :to="subItem.link"
              :class="{ 'active': isActive(subItem.link) }"
              @click="linkClicked($event, false)"
            >
              <span class="nav-main-link-name">{{ subItem.name }}</span>
            </RouterLink>
          </li>
        </ul>
      </template>
    </li>
  </ul>
</template>

<style lang="scss">
.nav-main {
  .nav-main-link {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.25rem;
    color: var(--app-text);
    text-decoration: none;
    transition: all 0.15s ease;
    
    &:hover {
      color: var(--app-ink);
      background-color: var(--app-surface-2);
    }
    
    &.active {
      color: var(--app-ink);
      background-color: var(--app-surface-2);
      font-weight: 600;
    }
    
    .nav-main-link-icon {
      margin-right: 0.75rem;
      width: 1.25rem;
      text-align: center;
    }
    
    .nav-main-link-name {
      flex: 1;
    }
  }
  
  .nav-main-link-submenu {
    .fa-chevron-down {
      margin-right: 0;
      margin-left: auto;
      transition: transform 0.15s ease;
    }
  }
  
  .nav-main-item.open {
    > .nav-main-link-submenu {
      .fa-chevron-down {
        transform: rotate(180deg);
      }
    }
    
    > .nav-main-submenu {
      display: block;
    }
  }
  
  .nav-main-submenu {
    display: none;
    padding-left: 1.5rem;
    
    .nav-main-link {
      padding: 0.5rem 1.25rem;
      font-size: 0.875rem;
    }
  }
}

// Темная тема
.nav-main-dark {
  .nav-main-link {
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    &.active {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
}

// Горизонтальное меню
.nav-main-horizontal {
  display: flex;
  
  .nav-main-item {
    position: relative;
    
    &:hover {
      > .nav-main-submenu {
        display: block;
      }
    }
  }
  
  .nav-main-submenu {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 200px;
    background: var(--app-surface);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    z-index: 1000;
    
    .nav-main-link {
      white-space: nowrap;
    }
  }
}
</style>